import { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, Wand2, Download, AlertTriangle } from 'lucide-react';
import {
  OUR_FIELDS,
  FIELD_TRANSFORMS,
  resolveField,
  getByPath,
  autoMatchMappings,
  mergeMappings,
  type OurField,
  type FieldTransform,
  type SupplierFieldMapping,
} from '@aggregator/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/api/client';
import type { SupplierProbeResult } from '@/api/types';

const REQUIRED_FIELDS = new Set<OurField>(['externalId', 'name', 'price']);
const NONE = '__none__';

interface Props {
  mappings: SupplierFieldMapping[];
  onChange: (mappings: SupplierFieldMapping[]) => void;
  /** Runs the probe against the API and returns the sample response + auto-match. */
  onFetch: () => Promise<SupplierProbeResult>;
  /** Extra description under the header. */
  hint?: string;
}

function shortValue(v: unknown): string {
  if (v === undefined) return '—';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 60 ? `${s.slice(0, 59)}…` : s;
}

export function FieldMappingEditor({ mappings, onChange, onFetch, hint }: Props) {
  const [probe, setProbe] = useState<SupplierProbeResult | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [lastAutoMatch, setLastAutoMatch] = useState<{ matched: number; unmatched: OurField[] } | null>(null);

  const sampleItem = useMemo(() => probe?.sampleItems?.[0] ?? null, [probe]);
  const byField = useMemo(
    () => new Map(mappings.map((m) => [m.ourField, m] as const)),
    [mappings],
  );

  async function runFetch() {
    setFetching(true);
    setFetchError(null);
    setLastAutoMatch(null);
    try {
      const result = await onFetch();
      setProbe(result);
      if (!result.ok) setFetchError(result.error || 'Request failed');
      else if (result.itemCount === 0) setFetchError('Connected, but no product array was found in the response.');
      else setRawOpen(false);
    } catch (err) {
      setFetchError(errorMessage(err));
      setProbe(null);
    } finally {
      setFetching(false);
    }
  }

  function setField(ourField: OurField, patch: { supplierPath?: string; transform?: FieldTransform | null; multiplier?: number | null }) {
    const next = mappings.filter((m) => m.ourField !== ourField);
    const current = byField.get(ourField);
    const supplierPath = patch.supplierPath ?? current?.supplierPath ?? '';
    let transform: FieldTransform | undefined =
      patch.transform === null ? undefined : patch.transform ?? current?.transform;
    if (supplierPath.trim() === '') {
      onChange(next); // clearing the path removes the mapping
      return;
    }
    const entry: SupplierFieldMapping = { ourField, supplierPath: supplierPath.trim() };
    if (transform) entry.transform = transform;
    const multiplier = patch.multiplier === null ? undefined : patch.multiplier ?? current?.multiplier;
    if (multiplier != null) entry.multiplier = multiplier;
    onChange([...next, entry]);
  }

  function applyAutoMatch(overwrite: boolean) {
    if (!sampleItem) return;
    const result = autoMatchMappings(sampleItem, { existing: mappings, overwrite });
    onChange(mergeMappings(mappings, result.mappings, { overwrite }));
    setLastAutoMatch({ matched: result.matchedCount, unmatched: result.unmatched });
  }

  const autoEntry = (f: OurField) => probe?.autoMatch?.entries.find((e) => e.ourField === f);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">API response &amp; field mapping</p>
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" loading={fetching} onClick={runFetch}>
            <Download className="h-4 w-4" /> Fetch sample response
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!sampleItem}
            onClick={() => applyAutoMatch(false)}
          >
            <Wand2 className="h-4 w-4" /> Auto-match fields
          </Button>
        </div>
      </div>

      {/* status line */}
      {fetchError ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            {fetchError}
            {probe?.status ? <span className="ml-1 opacity-70">(HTTP {probe.status})</span> : null}
          </div>
        </div>
      ) : null}

      {probe?.ok ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="success">
            <CheckCircle2 className="mr-1 h-3 w-3" /> {probe.latencyMs} ms
          </Badge>
          <span>
            <b className="text-foreground">{probe.itemCount}</b> items at{' '}
            <code className="rounded bg-muted px-1">{probe.itemsPath || 'response root'}</code>
          </span>
          {typeof probe.balance === 'number' ? <span>· balance {probe.balance.toFixed(2)} USDT</span> : null}
          {probe.rawTruncated ? <span>· response truncated</span> : null}
        </div>
      ) : null}

      {lastAutoMatch ? (
        <div className="rounded-md border border-success/40 bg-success/10 p-3 text-sm">
          ✓ Auto-matched <b>{lastAutoMatch.matched}</b> field{lastAutoMatch.matched === 1 ? '' : 's'}.
          {lastAutoMatch.unmatched.length > 0 ? (
            <>
              {' '}
              <span className="text-warning">
                Set manually: {lastAutoMatch.unmatched.join(', ')}
              </span>
            </>
          ) : (
            ' Everything matched.'
          )}
          {sampleItem ? (
            <button type="button" className="ml-2 text-xs text-primary hover:underline" onClick={() => applyAutoMatch(true)}>
              re-run &amp; overwrite manual edits
            </button>
          ) : null}
        </div>
      ) : null}

      {/* mapping grid */}
      <div className="overflow-x-auto rounded-md border scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 text-left">Our field</th>
              <th className="px-3 py-2 text-left">Supplier path</th>
              <th className="px-3 py-2 text-left">Multiplier</th>
              <th className="px-3 py-2 text-left">Transform</th>
              <th className="px-3 py-2 text-left">Result from sample</th>
            </tr>
          </thead>
          <tbody>
            {OUR_FIELDS.map((f) => {
              const m = byField.get(f);
              const path = m?.supplierPath ?? '';
              const transform = m?.transform;
              const suggestion = autoEntry(f);
              const transformed =
                sampleItem != null && path
                  ? resolveField(sampleItem, path, transform)
                  : undefined;
              const resolved = m?.multiplier == null ? transformed : Number(transformed) * m.multiplier;
              const rawResolved = sampleItem != null && path ? getByPath(sampleItem, path) : undefined;
              const missing = !path;
              return (
                <tr key={f} className={cn('border-b last:border-0', missing && REQUIRED_FIELDS.has(f) && 'bg-destructive/5')}>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-1 font-medium">
                      {f}
                      {REQUIRED_FIELDS.has(f) ? <span className="text-destructive">*</span> : null}
                    </div>
                    {suggestion && suggestion.confidence !== 'none' && !path ? (
                      <button
                        type="button"
                        className="mt-1 text-xs text-primary hover:underline"
                        onClick={() =>
                          setField(f, {
                            supplierPath: suggestion.supplierPath ?? '',
                            transform: suggestion.transform ?? null,
                          })
                        }
                      >
                        use “{suggestion.supplierPath}”
                      </button>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Input
                      value={path}
                      placeholder={suggestion?.supplierPath ?? 'e.g. data.items[0].price'}
                      onChange={(e) => setField(f, { supplierPath: e.target.value })}
                      className="h-8 font-mono text-xs"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    {f === 'price' ? (
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        value={m?.multiplier ?? 1}
                        onChange={(e) => setField(f, { multiplier: Number(e.target.value) })}
                        className="h-8 w-28 font-mono text-xs"
                        title="Use 0.01 when the supplier returns cents"
                      />
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Select
                      value={transform ?? NONE}
                      onChange={(e) =>
                        setField(f, { transform: e.target.value === NONE ? null : (e.target.value as FieldTransform) })
                      }
                      options={[
                        { value: NONE, label: '— none —' },
                        ...FIELD_TRANSFORMS.map((t) => ({ value: t, label: t })),
                      ]}
                      className="h-8 text-xs"
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    {sampleItem == null ? (
                      <span className="text-xs text-muted-foreground">fetch a sample →</span>
                    ) : !path ? (
                      <span className="text-xs text-muted-foreground">not mapped</span>
                    ) : rawResolved === undefined ? (
                      <span className="text-xs text-warning">path not found in sample</span>
                    ) : (
                      <span className="text-xs">
                        <code className="rounded bg-muted px-1">{shortValue(resolved)}</code>
                        {JSON.stringify(rawResolved) !== JSON.stringify(resolved) ? (
                          <span className="text-muted-foreground"> ← {shortValue(rawResolved)}</span>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        <span className="text-destructive">*</span> required. Leave a path blank to skip that field.
      </p>

      {/* raw response */}
      {probe?.ok ? (
        <div className="rounded-md border">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium"
            onClick={() => setRawOpen((o) => !o)}
          >
            {rawOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Raw response {probe.sampleItems && probe.sampleItems.length > 1 ? '(showing first item + full body)' : ''}
          </button>
          {rawOpen ? (
            <pre className="max-h-80 overflow-auto border-t bg-muted/30 p-3 text-xs scrollbar-thin">
              {typeof probe.raw === 'string' ? probe.raw : JSON.stringify(probe.raw, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
